import React, { useState } from 'react';
import { useAuth } from './auth';
import { Field, Input, Select } from '../ui/Form';
import { Button } from '../ui/PageShell';
import { STUDENT_PORTAL_URL } from './studentPortal';
import { CREDIT_LINE, DEVELOPER } from './about';
import './login.css';

const ROLES = [
  { value: 'admin', label: 'مدير' },
  { value: 'register', label: 'موجه' },
  { value: 'teacher', label: 'معلم' },
];

/** One login screen for every role: name + role -> code -> in. */
export default function Login() {
  const { lookup, select, verify } = useAuth();
  const [step, setStep] = useState('name'); // name | choose | code
  const [name, setName] = useState('');
  const [role, setRole] = useState('admin');
  const [candidates, setCandidates] = useState([]);
  const [userId, setUserId] = useState(null);
  const [masked, setMasked] = useState('');
  const [devCode, setDevCode] = useState('');
  const [accessCode, setAccessCode] = useState(false);
  const [smsFailed, setSmsFailed] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const applySent = (data) => {
    setUserId(data.user_id);
    setMasked(data.phone_masked || '');
    setDevCode(data.dev_code || '');
    // The API skips sending an SMS entirely for someone with a
    // رمز دخول (they can't receive one anyway — see access-codes.js) —
    // sent:false there, so the screen must say "enter your code", not
    // repeat the SMS message it never actually sent.
    setAccessCode(!!data.access_code);
    setSmsFailed(!!data.sms_failed);
    setStep('code');
  };

  const submitName = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('يرجى إدخال الاسم');
    setBusy(true);
    try {
      const data = await lookup(name.trim(), role);
      if (data.ambiguous) { setCandidates(data.candidates); setStep('choose'); }
      else applySent(data);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const pick = async (candidateId) => {
    setError(''); setBusy(true);
    try { applySent(await select(candidateId)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) return setError('يرجى إدخال الرمز');
    setBusy(true);
    try { await verify(userId, code.trim()); }
    catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo">م</div>
          <div>
            <h1>بوابة المتميزون</h1>
            <p>نظام إدارة المؤسسة التعليمية</p>
          </div>
        </div>

        {error && <div className="login-error">{error}</div>}

        {step === 'name' && (
          <form onSubmit={submitName} className="login-form">
            <Field label="الاسم الكامل" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="كما هو مسجّل في المدرسة" autoFocus />
            </Field>
            <Field label="نوع الحساب" required>
              <Select value={role} onChange={(e) => setRole(e.target.value)} options={ROLES} />
            </Field>
            <Button type="submit" disabled={busy}>{busy ? 'جارٍ البحث...' : 'متابعة'}</Button>
            <p className="login-portal">
              طالب أو ولي أمر؟ <a href={STUDENT_PORTAL_URL}>ادخل من بوابة الطالب</a>
            </p>
          </form>
        )}

        {step === 'choose' && (
          <div className="login-form">
            <p className="login-hint">يوجد أكثر من حساب بهذا الاسم، اختر الحساب الصحيح:</p>
            {candidates.map((c) => (
              <button key={c.user_id} type="button" className="login-candidate" disabled={busy} onClick={() => pick(c.user_id)}>
                {c.grade_title ? `${c.grade_title} — ${c.section_title || ''}` : c.phone_hint}
              </button>
            ))}
            <Button variant="secondary" type="button" onClick={() => setStep('name')}>رجوع</Button>
          </div>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} className="login-form">
            <p className="login-hint">
              {accessCode
                ? 'أدخل رمز الدخول الخاص بك (الرمز الذي أعطاك إياه المكتب).'
                : smsFailed ? 'تعذّر إرسال رسالة إلى الهاتف المسجّل. إن كان لديك رمز فأدخله، أو راجع إدارة المدرسة.'
                : `تم إرسال رمز التحقق إلى الهاتف ${masked}`}
              {devCode && <><br /><strong>رمز التطوير: {devCode}</strong></>}
            </p>
            <Field label={accessCode ? 'رمز الدخول' : 'رمز التحقق'} required>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" inputMode="numeric" maxLength={6} autoFocus dir="ltr" />
            </Field>
            <Button type="submit" disabled={busy}>{busy ? 'جارٍ التحقق...' : 'تسجيل الدخول'}</Button>
            <Button variant="secondary" type="button" onClick={() => { setStep('name'); setCode(''); }}>تغيير الاسم</Button>
          </form>
        )}
      </div>
      <footer className="login-credit">
        <a href={DEVELOPER.website} target="_blank" rel="noopener noreferrer">{CREDIT_LINE}</a>
      </footer>
    </div>
  );
}
